/**
 * EditToolbar Component
 * Phase 8: Photo Editing Components
 *
 * Toolbar with tool selection (crop, light) and action buttons (save, cancel, preview)
 */

import { Button } from '../../atoms/Button/Button';
import { SurfaceProvider } from '@/theme/SurfaceContext';

export type ToolType = 'crop' | 'light';

export interface EditToolbarProps {
  /** Current active tool */
  activeTool: ToolType;
  /** Callback when tool changes */
  onToolChange: (tool: ToolType) => void;
  /** Callback when save is clicked */
  onSave: () => void;
  /** Callback when cancel is clicked */
  onCancel: () => void;
  /** Callback when preview toggle is clicked */
  onPreviewToggle: () => void;
  /** Whether preview is currently shown */
  showPreview: boolean;
  /** Whether save button should be enabled */
  canSave: boolean;
  /** Whether save operation is in progress */
  isSaving: boolean;
  /** Optional title text */
  title?: string;
  /** Optional subtitle text */
  subtitle?: string;
}

export function EditToolbar({
  activeTool,
  onToolChange,
  onSave,
  onCancel,
  onPreviewToggle,
  showPreview,
  canSave,
  isSaving,
  title = 'Suggest Edit',
  subtitle,
}: EditToolbarProps) {
  return (
    <SurfaceProvider level="base" theme="light">
      <div className="flex items-center justify-between px-6 py-4 bg-surface-1 border-b border-border">
        <div className="flex items-center gap-6">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            {subtitle && <p className="text-sm text-muted mt-1">{subtitle}</p>}
          </div>

          {/* Tool Selector */}
          <div className="flex gap-2" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTool === 'crop'}
              aria-controls="crop-panel"
              onClick={() => onToolChange('crop')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                activeTool === 'crop'
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-surface-2 text-muted hover:bg-muted/20'
              }`}
            >
              Crop
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTool === 'light'}
              aria-controls="light-panel"
              onClick={() => onToolChange('light')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                activeTool === 'light'
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-surface-2 text-muted hover:bg-muted/20'
              }`}
            >
              Light
            </button>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onPreviewToggle}
            disabled={!canSave}
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </Button>

          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel (Esc)
          </Button>

          <Button
            type="button"
            variant="primary"
            onClick={onSave}
            disabled={!canSave || isSaving}
            loading={isSaving}
          >
            Save Edit
          </Button>
        </div>
      </div>
    </SurfaceProvider>
  );
}
